import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import axios from "axios";
import { BASELINE } from "../../util";
import toast from "react-hot-toast";

function SettingModal({ isOpen, onClose, user, onSave, onLogout }) {
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [premium, setPremium] = useState(user?.premium === 1);
  const [schoolGrade, setSchoolGrade] = useState(user?.school_grade || "");
  const [uid, setUid] = useState(user?.u_id || "");

  // 읽기 전용
  const [role] = useState(user?.role || "student");
  const [startPremiumDate] = useState(
    user?.start_premium_date ? user.start_premium_date : "N/A"
  );

  const [endPremiumDate] = useState(
    user?.end_premium_date ? user.end_premium_date : "N/A"
  );

  const formatDate = (dateString) => {
    if (dateString === "N/A") return dateString;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    return `${month}, ${day}, ${year}`;
  };

  useEffect(() => {
    setFirstName(user?.first_name || "");
    setLastName(user?.last_name || "");
    setPremium(user?.premium === 1);
    setSchoolGrade(user?.school_grade || "");
    setUid(user?.u_id || "");
  }, [user, isOpen]);

  const handleSave = async () => {
    try {
      const updatedUser = {
        u_id: user?.u_id, // 필수
        ...(firstName && { first_name: firstName }),
        ...(lastName && { last_name: lastName }),
        ...(premium !== undefined && { premium: premium ? 1 : 0 }),
        ...(schoolGrade && { school_grade: Number(schoolGrade) }),
      };

      const response = await axios.post(BASELINE + "user/info/update", updatedUser);

      if (response.status === 200) {
        toast.success("User information updated successfully!");
        window.location.reload();
        onSave(updatedUser);
        onClose();
      }
    } catch (error) {
      console.error("Error updating user information:", error);
      toast.error("Failed to update user information. Please try again.");
    }
  };

  const handleCopy = () => {
    navigator.clipboard
      .writeText(uid)
      .then(() => toast.success("UID copied to clipboard!"))
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy UID");
      });
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        {/* 반투명 배경 */}
        <Transition.Child
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        {/* 모달 내용 */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-6">
              <Dialog.Title className="text-xl font-bold text-slate-800 mb-5">
                Settings
              </Dialog.Title>

              <div className="space-y-4">
                {/* UID */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">UID</label>
                  <input
                    type="text"
                    value={uid}
                    readOnly
                    onClick={handleCopy}
                    title="Click to copy"
                    className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-600 cursor-pointer hover:bg-slate-200 focus:outline-none transition"
                  />
                </div>

                {/* First Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  />
                </div>

                {/* Role */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Role</label>
                  <input
                    type="text"
                    value={role}
                    disabled
                    className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed"
                  />
                </div>

                {/* School Grade */}
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">School Grade</label>
                  {role === "teacher" ? (
                    <input type="number" value={0} disabled
                      className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed" />
                  ) : (
                    <input
                      type="number"
                      value={schoolGrade}
                      onChange={(e) => setSchoolGrade(e.target.value)}
                      className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                  )}
                </div>

                {/* Premium Dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Premium Start</label>
                    <input type="text" value={formatDate(startPremiumDate) || ""} disabled
                      className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Premium End</label>
                    <input type="text" value={formatDate(endPremiumDate) || ""} disabled
                      className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400 cursor-not-allowed" />
                  </div>
                </div>
              </div>

              {/* 버튼 */}
              <div className="mt-6 flex justify-between items-center">
                <button
                  className="px-4 py-2 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 border border-red-200 transition"
                  onClick={onLogout}
                >
                  Logout
                </button>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 transition"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 transition shadow-sm"
                    onClick={handleSave}
                  >
                    Update
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default SettingModal;
